import { AiChatRequest, AiChatResponse, AiChatMessage, AiChatPaginatedTableResult } from "@app/projects/network-assessment/provider-hub/interfaces";
import ServiceBase from "@app/shared/serviceBase";

export default class ChatbotService extends ServiceBase {

  /**
   * Posts a conversation to be processed by Snowflake Cortex. The last prompt in the conversation must be unanswered.
   * The API responds with the last prompt answered by the LLM.
   */
  public async sendConversation(conversation: AiChatRequest): Promise<AiChatResponse> {
    return await this.makePost<AiChatResponse>(`chatbot/response`, conversation);
  }

  public async downloadCsvMessageContents(message: AiChatMessage): Promise<string> {
    const response = await this.makePost<Blob>(
      "chatbot/message-contents-csv",
      message,
      undefined,
      false,
      "application/json",
      true,
      { responseType: 'blob' }
    );

    return window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
  }

  /**
   * Downloads a CSV of the full (non-limited) SQL result set underlying a previously generated paginated AI table response.
   */
  public async downloadTableResultsCsv(
    responseId: number,
    threadId: string,
    projectRunConfigurationId: number
  ): Promise<string> {
    const queryString = `threadId=${encodeURIComponent(threadId)}&projectRunConfigurationId=${projectRunConfigurationId}`;
    const response = await this.makeGet<Blob>(
      `chatbot/table-results/${responseId}/csv`,
      queryString,
      undefined,
      true,
      { responseType: 'blob' }
    );

    return window.URL.createObjectURL(new Blob([response], { type: 'text/csv' }));
  }

  /**
   * GET conversation history for a thread id.
   */
  public async getConversation(threadId: string): Promise<AiChatResponse> {
    return await this.makeGet<AiChatResponse>(`chatbot/thread/${encodeURIComponent(threadId)}`);
  }

  /**
   * Gets the count of successful Quinn responses for a client in the current calendar week.
   */
  public async getWeeklyPromptUsage(clientId: number): Promise<number> {
    return await this.makeGet<number>(`chatbot/weekly-prompt-usage/${clientId}`);
  }

  /**
   * GET paginated table results for a previously generated AI table response.
   */
  public async getTableResults(
    responseId: number,
    threadId: string,
    projectRunConfigurationId: number,
    page: number,
    pageSize: number,
    sortColumn?: string,
    sortDirection?: string
  ): Promise<AiChatPaginatedTableResult> {
    let queryString = `threadId=${encodeURIComponent(threadId)}&projectRunConfigurationId=${projectRunConfigurationId}&page=${page}&pageSize=${pageSize}`;
    if (sortColumn) {
      queryString += `&sortColumn=${encodeURIComponent(sortColumn)}&sortDirection=${encodeURIComponent(sortDirection ?? 'asc')}`;
    }
    return await this.makeGet<AiChatPaginatedTableResult>(
      `chatbot/table-results/${responseId}`,
      queryString
    );
  }
}

